import React, { useEffect, useMemo, useRef, useState } from "react"

import type { Session } from "@supabase/supabase-js"
import { useQueryClient } from "@tanstack/react-query"

import {
  type AuthContextValue,
  type AuthUser,
  AuthContext,
  capabilitiesFor,
  type Role,
  STAFF_EMAIL_DOMAIN,
} from "@/contexts/AuthContext"
import { edgeFunctionError } from "@/lib/edgeFunctionError"
import { supabase } from "@/lib/supabase"

/**
 * Read the role, and a partner's operator, from the access token's claims.
 *
 * They have to come from the token, not from `session.user.app_metadata`. That
 * property is built from the stored GoTrue user record, and the access token
 * hook injects its claims into the *issued JWT* rather than writing them
 * back to the record — so the two genuinely differ, and reading the user object
 * returns undefined for everybody.
 *
 * Verifying the signature is the API's job; PostgREST and the edge functions
 * re-check the real token on every request, and the functions go further and
 * read the access list itself. This decode only decides what the UI renders, so
 * a tampered token buys nothing beyond a broken-looking page.
 */
function accessFromToken(accessToken: string | undefined): {
  role: Role | null
  operator: string | null
} {
  const none = { role: null, operator: null }
  if (!accessToken) return none
  try {
    const payload = accessToken.split(".")[1]
    if (!payload) return none
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/")
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4)
    const json = new TextDecoder().decode(
      Uint8Array.from(atob(padded), (c) => c.charCodeAt(0))
    )
    const claims = (
      JSON.parse(json) as {
        app_metadata?: { collections_role?: unknown; collections_operator?: unknown }
      }
    ).app_metadata
    const claimed = claims?.collections_role
    if (claimed === "admin" || claimed === "operator") return { role: claimed, operator: null }
    // A partner with no operator has nothing it could be shown, so it is no role at all rather
    // than a partner who sees an empty board with no explanation.
    const operator = claims?.collections_operator
    if (claimed === "partner" && typeof operator === "string" && operator) {
      return { role: "partner", operator }
    }
    return none
  } catch {
    return none
  }
}

function toAuthUser(session: Session | null): AuthUser | null {
  if (!session?.user) return null
  const { user } = session
  const meta = (user.user_metadata ?? {}) as Record<string, unknown>
  const { role, operator } = accessFromToken(session.access_token)
  const fullName =
    (typeof meta.full_name === "string" && meta.full_name) ||
    (typeof meta.name === "string" && meta.name) ||
    user.email ||
    null

  return { id: user.id, email: user.email ?? null, full_name: fullName, role, operator }
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const queryClient = useQueryClient()
  const signedInAs = useRef<string | null>(null)

  useEffect(() => {
    /**
     * Drop everything cached when the person changes.
     *
     * Query keys name a date or a route, not who asked, so what one login fetched would be
     * handed straight to the next from cache: a partner signing in after a HIVED user on the
     * same tab would be shown every operator's routes, and their costs, until the refetch landed.
     * A token refresh keeps the same user and so keeps the cache.
     */
    const adopt = (next: Session | null) => {
      const userId = next?.user?.id ?? null
      if (signedInAs.current !== null && signedInAs.current !== userId) queryClient.clear()
      signedInAs.current = userId
      setSession(next)
      setLoading(false)
    }

    supabase.auth.getSession().then(({ data }) => adopt(data.session))

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => adopt(next))

    return () => subscription.unsubscribe()
  }, [queryClient])

  const value = useMemo<AuthContextValue>(() => {
    const user = toAuthUser(session)
    return {
      user,
      session,
      loading,
      isAuthorised: user?.role != null,
      isAdmin: user?.role === "admin",
      isPartner: user?.role === "partner",
      can: capabilitiesFor(user?.role ?? null),
      signInWithGoogle: async () => {
        await supabase.auth.signInWithOAuth({
          provider: "google",
          options: {
            redirectTo: `${window.location.origin}/collections/monitoring`,
            // Restrict the Google account chooser to the HIVED workspace.
            // This is a UX hint only — the real gate is the server-side
            // domain check on the auth hook.
            queryParams: { hd: STAFF_EMAIL_DOMAIN, prompt: "select_account" },
          },
        })
      },
      signInWithPassword: async (email, password) => {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw new Error(error.message)
      },
      signUp: async ({ email, password }) => {
        // The function makes the account, and only for an address an admin has added. It cannot
        // hand back a session, so signing in is a second step with what was just typed.
        const { data, error } = await supabase.functions.invoke("collections-partner-signup", {
          body: { email, password },
        })
        if (error) {
          throw new Error((await edgeFunctionError(error)) ?? "We couldn't create your account.")
        }
        if ((data as { success?: boolean } | null)?.success === false) {
          throw new Error((data as { error?: string }).error ?? "We couldn't create your account.")
        }
        const signedIn = await supabase.auth.signInWithPassword({ email, password })
        if (signedIn.error) throw new Error(signedIn.error.message)
      },
      changePassword: async (password) => {
        const { error } = await supabase.auth.updateUser({ password })
        if (error) throw new Error(error.message)
      },
      signOut: async () => {
        await supabase.auth.signOut()
      },
    }
  }, [session, loading])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
